import { ref, readonly } from 'vue'
import type { 
  GpuStatistics, 
  ParsedInput, 
  Flavor, 
  TierData, 
  FlavorResult,
  ProcessedTierResult,
  GpuTrainingResults
} from '~/types/gpu'
import { GPU_COMPUTE_POWER } from '~/types/gpu'

export const useGpuCalculation = () => {
  const { t } = useI18n()
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  
  const fetchGpuStatistics = async (): Promise<GpuStatistics> => {
    const response = await $fetch<GpuStatistics>('/api/gpu-statistics')
    return response
  }

  const calculateTrainingDays = (
    X: number, 
    Y: number, 
    stockQuantity: number, 
    gpuType: string
  ): number => {
    const W = stockQuantity
    const computePower = GPU_COMPUTE_POWER[gpuType] ?? 1e14
    const Z = (1.45 * 8 * X * Y * 1e24) / (W * computePower * 1e12 * 86400)
    return Z
  }

  const calculateTotalCost = (Z: number, monthlyFee: string, stockQuantity: number): number => {
    const fee = parseFloat(monthlyFee)
    const totalCost = fee * stockQuantity * (Z / 30)
    return parseFloat(totalCost.toFixed(2))
  }

  const formatTrainingTime = (days: number): string => {
    if (days < 1) {
      const hours = Math.round(days * 24 * 10) / 10
      return `${hours} ${t('gpu.hours')}`
    }
    return `${Math.round(days * 10) / 10} ${t('gpu.days')}`
  }

  const processTier = (tierData: TierData, X: number, Y: number): ProcessedTierResult => {
    const allFlavors: Flavor[] = [
      ...(tierData.highest_total_stock_gpu_type_flavors || []),
      ...(tierData.cheapest_monthly_fee_flavor ? [tierData.cheapest_monthly_fee_flavor] : []),
      ...(tierData.highest_stock_flavor ? [tierData.highest_stock_flavor] : [])
    ]

    if (!allFlavors.length) {
      throw new Error('無有效的 flavor 可選擇，請檢查 gpu_statistics.json 資料')
    }

    const validFlavors = allFlavors.filter(flavor =>
      flavor && ['name', 'gpu_type', 'gpu_number', 'stock_quantity', 'monthly_fee'].every(
        key => (flavor as any)[key] !== undefined
      )
    )

    if (!validFlavors.length) {
      throw new Error('無有效的 flavor 可選擇，請檢查 gpu_statistics.json 資料')
    }

    const calculatedFlavors = validFlavors.map(flavor => {
      const Z = calculateTrainingDays(X, Y, flavor.stock_quantity, flavor.gpu_type)
      const cost = calculateTotalCost(Z, flavor.monthly_fee, flavor.stock_quantity)
      return { ...flavor, Z, cost }
    })

    const cheapestFlavor = calculatedFlavors.reduce((prev, current) =>
      (prev.cost < current.cost) ? prev : current
    )

    const fastestFlavor = calculatedFlavors.reduce((prev, current) =>
      (prev.Z < current.Z) ? prev : current
    )

    if (!cheapestFlavor) {
      throw new Error('無法選擇最低成本的 flavor，請檢查 gpu_statistics.json 資料')
    }
    if (!fastestFlavor) {
      throw new Error('無法選擇最快訓練的 flavor，請檢查 gpu_statistics.json 資料')
    }

    const mapToResult = (flavor: typeof calculatedFlavors[0]): FlavorResult => ({
      name: flavor.name,
      gpu_type: flavor.gpu_type,
      gpu_number: flavor.gpu_number,
      stock_quantity: flavor.stock_quantity,
      training_days: parseFloat(flavor.Z.toFixed(2)),
      training_time_display: formatTrainingTime(flavor.Z),
      total_cost: flavor.cost
    })

    return {
      "Cheapest Flavor": mapToResult(cheapestFlavor),
      "Fastest Flavor": mapToResult(fastestFlavor)
    }
  }

  const parseInputToJson = async (rawText: string): Promise<ParsedInput> => {
    const trimmedText = rawText.trim()
    
    try {
      const response = await $fetch<{ X: number, Y: number }>('/api/parse-gpu-input', {
        method: 'POST',
        body: { text: trimmedText }
      })
      
      if (!response || typeof response.X !== 'number' || typeof response.Y !== 'number') {
        throw new Error(`輸入格式錯誤，實際輸入: '${trimmedText}'，應為：'我想要訓練一個模型：模型名稱, 模型參數<數字>B|T, 訓練集 Token 數 <數字>B|T。請提供我適合的運算資源選項。'，例如 '我想要訓練一個模型：Llama 3.1, 模型參數70B, 訓練集 Token 數 1.5T。請提供我適合的運算資源選項。'`)
      }

      return response
    } catch (e: any) {
      throw new Error(`解析失敗，實際輸入: '${trimmedText}'，應為：'我想要訓練一個模型：模型名稱, 模型參數<數字>B|T, 訓練集 Token 數 <數字>B|T。請提供我適合的運算資源選項。'，例如 '我想要訓練一個模型：Llama 3.1, 模型參數70B, 訓練集 Token 數 1.5T。請提供我適合的運算資源選項。'`)
    }
  }

  const calculateGpuTraining = async (input: string): Promise<GpuTrainingResults> => {
    isLoading.value = true
    error.value = null
    
    try {
      const parsedData = await parseInputToJson(input)
      const gpuStatistics = await fetchGpuStatistics()
      console.log("parsedData andy ===  : ",parsedData)
      const output: GpuTrainingResults = {
        "Advanced Tier": processTier(gpuStatistics.content.advanced, parsedData.X, parsedData.Y),
        "Intermediate Tier": processTier(gpuStatistics.content.intermediate, parsedData.X, parsedData.Y)
      }
      
      return output
    } catch (e: any) {
      error.value = e.message
      throw e
    } finally {
      isLoading.value = false
    }
  }

  const formatResultsTable = async (results: GpuTrainingResults): Promise<string> => {
    try {
      const response = await $fetch<{ table: string }>('/api/format-gpu-table', {
        method: 'POST',
        body: { results }
      })
      
      return response.table
    } catch (e: any) {
      console.error('格式化表格失敗:', e.message)
      return JSON.stringify(results, null, 2)
    }
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    calculateGpuTraining,
    formatResultsTable
  }
}
